/**
 * ========================================
 * 🎟️ TICKET V4-P0-007: Safe Fetch JSON
 * ========================================
 * Wrapper robusto para fetch() con manejo completo de errores,
 * correlationId tracking, timeout, y respuesta normalizada.
 * 
 * CARACTERÍSTICAS:
 * - ✅ Timeout configurable (default: 30s)
 * - ✅ Extracción automática de correlationId (header + body)
 * - ✅ Manejo de errores de red, JSON parsing, HTTP status
 * - ✅ Respuesta normalizada: { ok, status, data, error, correlationId }
 * - ✅ Logs estructurados para debugging
 * - ✅ Compatible con JWT Bearer token
 * 
 * USO:
 * ```js
 * const result = await safeFetchJson('/api/endpoint', {
 *   method: 'POST',
 *   body: { sessionId: 'xyz' },
 *   token: 'jwt-token-here',
 *   timeout: 30000
 * });
 * 
 * if (result.ok) {
 *   console.log('Éxito:', result.data, 'correlationId:', result.correlationId);
 * } else {
 *   console.error('Error:', result.error, 'status:', result.status);
 * }
 * ```
 */

/**
 * Realiza una petición HTTP fetch con manejo robusto de errores
 * 
 * @param {string} url - URL del endpoint (absoluta o relativa)
 * @param {Object} options - Opciones de configuración
 * @param {string} [options.method='GET'] - Método HTTP (GET, POST, PUT, DELETE)
 * @param {Object|null} [options.body=null] - Cuerpo de la petición (auto-stringified a JSON)
 * @param {string|null} [options.token=null] - JWT token (se agrega como Bearer automáticamente)
 * @param {number} [options.timeout=30000] - Timeout en milisegundos (default: 30s)
 * @param {Object} [options.headers={}] - Headers adicionales
 * @param {boolean} [options.verbose=false] - Activar logs detallados
 * 
 * @returns {Promise<Object>} Respuesta normalizada:
 *   - ok: boolean - true si HTTP 200-299
 *   - status: number - Código HTTP (200, 400, 500, 0 si error de red)
 *   - data: Object|null - Respuesta JSON parseada (null si error)
 *   - error: string|null - Mensaje de error (null si éxito)
 *   - correlationId: string|null - ID de correlación del backend
 *   - timestamp: number - Timestamp de la respuesta
 */
export async function safeFetchJson(url, options = {}) {
  const {
    method = 'GET',
    body = null,
    token = null,
    timeout = 30000,
    headers = {},
    verbose = false
  } = options;

  const startTime = Date.now();
  const requestId = `req-${startTime}-${Math.random().toString(36).substring(7)}`;
  
  if (verbose || window.VITE_EPL_VERBOSE) {
    console.log(`[safeFetchJson] 🚀 ${requestId} ${method} ${url}`, { body, timeout });
  }

  // Construir headers con Content-Type y Authorization
  const fetchHeaders = {
    'Content-Type': 'application/json',
    ...headers
  };

  if (token) {
    fetchHeaders['Authorization'] = token.startsWith('Bearer ') ? token : `Bearer ${token}`;
  }

  // Configurar timeout con AbortController
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), timeout);

  try {
    // Realizar fetch con timeout
    const response = await fetch(url, {
      method,
      headers: fetchHeaders,
      body: body ? JSON.stringify(body) : undefined,
      signal: controller.signal
    });

    clearTimeout(timeoutId);

    // Extraer correlationId del header (prioridad alta)
    let correlationId = response.headers.get('X-Correlation-Id') || 
                        response.headers.get('x-correlation-id') ||
                        null;

    // Intentar parsear JSON
    let data = null;
    let parseError = null;

    try {
      const text = await response.text();
      if (text) {
        data = JSON.parse(text);
        
        // Extraer correlationId del body si no vino en header
        if (!correlationId && data && typeof data === 'object') {
          correlationId = data.correlationId || data.correlation_id || null;
        }
      }
    } catch (err) {
      parseError = `JSON parse error: ${err.message}`;
      if (verbose || window.VITE_EPL_VERBOSE) {
        console.error(`[safeFetchJson] ❌ ${requestId} Parse error:`, err);
      }
    }

    const duration = Date.now() - startTime;
    const ok = response.ok; // true si 200-299
    const status = response.status;

    // Resultado normalizado
    const result = {
      ok,
      status,
      data,
      error: ok ? null : (data?.error || data?.message || parseError || `HTTP ${status}`),
      correlationId,
      timestamp: Date.now(),
      duration
    };

    if (verbose || window.VITE_EPL_VERBOSE) {
      console.log(`[safeFetchJson] ${ok ? '✅' : '⚠️'} ${requestId} ${status} (${duration}ms)`, {
        correlationId,
        error: result.error,
        dataPreview: data ? JSON.stringify(data).substring(0, 100) : null
      });
    }

    // Log estructurado para errores HTTP (4xx, 5xx)
    if (!ok) {
      console.error(`[safeFetchJson] ❌ ${method} ${url} failed`, {
        requestId,
        status,
        error: result.error,
        correlationId,
        duration,
        data: data || null
      });
    }

    return result;

  } catch (err) {
    clearTimeout(timeoutId);

    // Determinar tipo de error
    let errorType = 'NETWORK_ERROR';
    let errorMessage = err.message;

    if (err.name === 'AbortError') {
      errorType = 'TIMEOUT_ERROR';
      errorMessage = `Request timeout after ${timeout}ms`;
    } else if (err.message.includes('NetworkError') || err.message.includes('Failed to fetch')) {
      errorType = 'NETWORK_ERROR';
      errorMessage = 'Network error - check internet connection';
    }

    const duration = Date.now() - startTime;

    const result = {
      ok: false,
      status: 0, // 0 indica error de red/timeout
      data: null,
      error: `${errorType}: ${errorMessage}`,
      correlationId: null,
      timestamp: Date.now(),
      duration
    };

    console.error(`[safeFetchJson] ❌ ${requestId} ${errorType}`, {
      method,
      url,
      error: errorMessage,
      duration
    });

    return result;
  }
}

/**
 * Genera un correlationId único para rastreo de errores
 * Formato: CID-{timestamp}-{random}
 * 
 * @returns {string} correlationId único
 */
export function generateCorrelationId() {
  const timestamp = Date.now();
  const random = Math.random().toString(36).substring(2, 10);
  return `CID-${timestamp}-${random}`;
}

/**
 * Copia correlationId al portapapeles con feedback visual
 * 
 * @param {string} correlationId - ID a copiar
 * @returns {Promise<boolean>} true si se copió exitosamente
 */
export async function copyCorrelationId(correlationId) {
  try {
    await navigator.clipboard.writeText(correlationId);
    console.log('[safeFetchJson] 📋 CorrelationId copiado:', correlationId);
    return true;
  } catch (err) {
    console.error('[safeFetchJson] ❌ Error copiando correlationId:', err);
    
    // Fallback para navegadores sin clipboard API
    const input = document.createElement('input');
    input.value = correlationId;
    document.body.appendChild(input);
    input.select();
    const success = document.execCommand('copy');
    document.body.removeChild(input);
    
    return success;
  }
}

export default safeFetchJson;
